import React from 'react';
import { Facebook, Instagram, Mail, MapPin } from 'lucide-react';
import { SiteSettings, User } from '../types';

interface FooterProps {
    settings?: SiteSettings;
    onNavigate: (page: string) => void;
    user?: User | null;
}

const Footer: React.FC<FooterProps> = ({ settings, onNavigate, user }) => {
    // Default fallback if settings aren't fully loaded yet
    const email = settings?.email || 'rsgleamofgolds@gmail.com';
    const address = settings?.address || 'Luxury Lane, Makati City';

  const handleNav = (e: React.MouseEvent, page: string) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    onNavigate(page);
  };

  // Handler for links that should go to Order History for members, Transactions for admin, and Login for guests
  const handleCustomerCareLink = (e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    if (user?.role === 'admin') {
        onNavigate('admin-transactions');
    } else if (user) {
        onNavigate('orders');
    } else {
        // Redirect guests to login/register page
        onNavigate('auth');
    }
  };

  // Handler specifically for Returns & Verification to go to Refunds tab
  const handleReturnsLink = (e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    if (user?.role === 'admin') {
        onNavigate('admin-transactions');
    } else if (user) {
        onNavigate('refunds');
    } else {
        onNavigate('auth');
    }
  };

  return (
    <footer className="bg-stone-100 text-stone-600 pt-20 pb-10 border-t border-stone-200">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          
          {/* Brand */}
          <div className="space-y-6">
            <h4 className="text-stone-900 font-serif text-xl tracking-wide cursor-pointer" onClick={(e) => handleNav(e, 'home')}>Sparkling Gems</h4>
            <p className="text-xs leading-6 font-light">
              Curated luxury pieces in the Philippines. Authenticity-first listings, premium presentation, and a seamless member experience built for confident shopping.
            </p>
            <div className="flex gap-4">
              <a href="https://www.instagram.com/sparkling1gems" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full border border-stone-300 flex items-center justify-center hover:border-gold-600 hover:text-gold-600 transition-colors">
                <Instagram size={14} />
              </a>
              <a href="https://www.facebook.com/sparkling1gems" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full border border-stone-300 flex items-center justify-center hover:border-gold-600 hover:text-gold-600 transition-colors">
                <Facebook size={14} />
              </a>
            </div>
            {/* Added dynamic contact preview */}
            <div className="text-[10px] text-stone-400 mt-4">
                <p>{address}</p>
                <p>{email}</p>
            </div>
          </div>

          {/* Customer Care */}
          <div>
            <h5 className="text-stone-900 text-xs tracking-[0.2em] uppercase mb-6 font-bold">Customer Care</h5>
            <ul className="space-y-3 text-xs font-light">
              <li><button onClick={handleCustomerCareLink} className="hover:text-gold-600 transition-colors text-left">Ordering & Payments</button></li>
              <li><button onClick={handleCustomerCareLink} className="hover:text-gold-600 transition-colors text-left">Shipping (Philippines)</button></li>
              <li><button onClick={handleReturnsLink} className="hover:text-gold-600 transition-colors text-left">Returns & Verification</button></li>
              <li><button onClick={(e) => handleNav(e, 'contact')} className="hover:text-gold-600 transition-colors text-left">Contact Support</button></li>
              <li><button onClick={handleCustomerCareLink} className="hover:text-gold-600 transition-colors text-left">Track Order</button></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h5 className="text-stone-900 text-xs tracking-[0.2em] uppercase mb-6 font-bold">The Maison</h5>
            <ul className="space-y-3 text-xs font-light">
              <li><button onClick={(e) => handleNav(e, 'about')} className="hover:text-gold-600 transition-colors text-left">About Sparkling Gems</button></li>
              <li><button onClick={(e) => handleNav(e, 'services')} className="hover:text-gold-600 transition-colors text-left">Our Services</button></li>
              <li><button onClick={(e) => handleNav(e, 'about')} className="hover:text-gold-600 transition-colors text-left">Sustainability</button></li>
              <li><button onClick={(e) => handleNav(e, 'contact')} className="hover:text-gold-600 transition-colors text-left">Careers</button></li>
              <li><button onClick={(e) => handleNav(e, 'privacy-policy')} className="hover:text-gold-600 transition-colors text-left">Privacy Policy</button></li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
             <h5 className="text-stone-900 text-xs tracking-[0.2em] uppercase mb-6 font-bold">Newsletter</h5>
             <p className="text-xs font-light mb-4">Subscribe to receive updates, access to exclusive deals, and more.</p>
             <div className="flex border-b border-stone-300 pb-2">
               <input 
                  type="email" 
                  placeholder="Enter your email" 
                  className="bg-transparent w-full text-sm outline-none text-stone-900 placeholder-stone-400"
               />
               <button className="text-gold-600 hover:text-gold-500 uppercase text-xs tracking-wider">
                 Join
               </button>
             </div>
          </div>
        </div>

        <div className="border-t border-stone-200 pt-8 flex flex-col md:flex-row justify-between items-center text-[10px] text-stone-500">
          <p className="cursor-default select-none">
            &copy; 2026 Sparkling Gems, Philippines. All Rights Reserved.
          </p>
          <div className="flex gap-6 mt-4 md:mt-0">
             <span>Authentic Listings</span>
             <span>•</span>
             <span>Elegant Presentation</span>
             <span>•</span>
             <span>Member Wishlist</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;